import { useEffect, useRef, useState } from 'react';

export default function ContactSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);
  const maxChars = 500;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-fade-in').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitStatus === 'loading') return;
    const form = e.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, value.toString());
    });
    setSubmitStatus('loading');
    try {
      const res = await fetch('https://readdy.ai/api/form/d716uk4991ove783spj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 bg-green-950" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* 왼쪽 연락처 정보 — PC만 표시 */}
          <div className="section-fade-in hidden lg:block">
            <span className="inline-block bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-6 tracking-widest uppercase">
              Free Consultation
            </span>
            <h2 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
              무료 상담을<br />신청하세요
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-10">
              석면 관련 모든 문의에 신속하게 답변 드립니다.
              전문가가 직접 현장을 방문하여 정확한 상담을 제공합니다.
            </p>

            <div className="flex flex-col gap-5">
              {[
                { icon: 'ri-phone-line', label: '전화 문의', value: '031-8043-8611', sub: '평일 09:00 – 18:00' },
                { icon: 'ri-mail-line', label: '이메일 문의', value: 'sodamenc0728@daum.net', sub: '24시간 접수 가능' },
                { icon: 'ri-map-pin-line', label: '회사 위치', value: '경기도 화성시 만세구 꽃내음1길 5-4, 510호', sub: '주차 가능' },
              ].map((contact) => (
                <div key={contact.label} className="flex items-center gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl flex-shrink-0">
                    <i className={`${contact.icon} text-amber-400 text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-white/40 mb-0.5">{contact.label}</div>
                    <div className="text-white font-medium text-sm">{contact.value}</div>
                    <div className="text-xs text-white/40">{contact.sub}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* 모바일 상단 타이틀 */}
          <div className="section-fade-in lg:hidden">
            <span className="inline-block bg-amber-500/20 border border-amber-400/30 text-amber-300 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
              Free Consultation
            </span>
            <h2 className="text-3xl font-bold text-white leading-tight mb-2">
              무료 상담 신청
            </h2>
            <p className="text-white/60 text-sm leading-relaxed">
              석면 관련 모든 문의에 신속하게 답변 드립니다.
            </p>
          </div>

          <div className="section-fade-in">
            {submitStatus === 'success' ? (
              <div className="bg-white/10 border border-white/20 rounded-3xl p-12 text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-green-400 text-3xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">상담 신청 완료!</h3>
                <p className="text-white/60 text-sm leading-relaxed mb-6">
                  문의가 접수되었습니다. 영업일 기준 1~2일 내에
                  담당자가 연락 드리겠습니다.
                </p>
                <button
                  onClick={() => setSubmitStatus('idle')}
                  className="bg-amber-500 hover:bg-amber-400 text-green-950 font-bold px-6 py-3 rounded-full text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  다시 작성하기
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white/10 border border-white/20 rounded-3xl p-8"
              >
                <h3 className="text-xl font-bold text-white mb-6">상담 문의서</h3>
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs text-white/50 mb-1.5 font-medium">담당자 성함 *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="홍길동"
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/50 mb-1.5 font-medium">연락처 *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="010-0000-0000"
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1.5 font-medium">이메일</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="example@company.com"
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1.5 font-medium">회사명 / 기관명</label>
                    <input
                      type="text"
                      name="company"
                      placeholder="(주)회사명"
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1.5 font-medium">문의 서비스</label>
                    <select
                      name="service"
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-amber-400 transition-colors"
                    >
                      <option value="" className="bg-green-900">선택해주세요</option>
                      <option value="조사" className="bg-green-900">석면조사</option>
                      <option value="농도측정" className="bg-green-900">석면농도측정</option>
                      <option value="해체제거" className="bg-green-900">석면해체·제거</option>
                      <option value="감리" className="bg-green-900">해체·제거 감리</option>
                      <option value="안전관리" className="bg-green-900">석면건축물 안전관리</option>
                      <option value="기타" className="bg-green-900">기타</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-white/50 mb-1.5 font-medium">
                      문의 내용
                      <span className="ml-2 text-white/30">{charCount}/{maxChars}</span>
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={maxChars}
                      onChange={(e) => {
                        if (e.target.value.length > maxChars) {
                          e.target.value = e.target.value.slice(0, maxChars);
                        }
                        setCharCount(e.target.value.length);
                      }}
                      placeholder="건물 위치, 면적, 건축 연도 등 관련 정보를 함께 작성해 주시면 더욱 정확한 상담이 가능합니다."
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    ></textarea>
                  </div>
                  {submitStatus === 'error' && (
                    <p className="text-red-400 text-xs">전송 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitStatus === 'loading'}
                    className="w-full bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 text-green-950 font-bold py-4 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
                  >
                    {submitStatus === 'loading' ? '전송 중...' : '상담 신청하기'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
